/* Cutting a document into sections that can be collapsed.

   A worked example is twenty-five blocks long. Read top to bottom it is exactly
   what it should be; *scanned*, it is a wall, and scanning is what a teacher
   does when they are deciding whether this is the example they want. So the
   page shows the headings and opens what is asked for.

   The cut follows how the material was actually written, not a guess:

     * where a document uses `rule` blocks, they are the author's own section
       breaks — the five planning steps are separated exactly that way;
     * where it does not, every titled block is its own section, which is the
       shape of the application examples (seven blocks, seven headings).

   The first block's title becomes the section heading and is lifted out of the
   block, so nothing is set twice.

   OWNED BY ITEM 4 OF docs/plan/00-prestavba-2026-08.md. */

import type { Block, Localized } from "../../lib/types";

/* Deliberately the same shape as Part in lib/types.ts: a section cut out of a
   flat document and a part written as one are the same thing to a reader, so
   they are the same thing to the renderer. */
export interface Section {
  title?: Localized;
  blocks: Block[];
}

function lift(group: Block[]): Section {
  const first = group[0];
  if (!first?.title) return { blocks: group };
  // The heading is the title; leaving it on the block too would print it twice.
  const { title, ...rest } = first;
  return { title, blocks: [rest as Block, ...group.slice(1)] };
}

export function sections(blocks: Block[]): Section[] {
  const useRules = blocks.some((b) => b.k === "rule");
  const out: Section[] = [];
  let current: Block[] = [];

  const flush = () => {
    if (current.length) out.push(lift(current));
    current = [];
  };

  for (const block of blocks) {
    if (useRules) {
      // The rule marked the break; the break is now the section boundary, so
      // the rule itself has nothing left to draw.
      if (block.k === "rule") { flush(); continue; }
    } else if (block.title && current.length) {
      flush();
    }
    current.push(block);
  }
  flush();
  return out;
}

/* The first block that carries a sample output — what actually comes out of
   the material, which is what a page about it should open with rather than a
   description of the target state. It is labelled with its own heading, so it
   is clear which step of the example it belongs to. */
export function preview(blocks: Block[]): { out: Localized; from: Localized | null } | null {
  for (const block of blocks) {
    const out = (block as { out?: Localized }).out;
    if (out) return { out, from: block.title ?? null };
  }
  return null;
}
